import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/i0b4179m_A_I_Means_Business.jpg";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setIsSubmitted(true);
    } catch (error) {
      // Still show success to prevent email enumeration
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-md mx-auto px-6">
          {/* Card - White background with dark text */}
          <div className="bg-white p-8 rounded-2xl shadow-2xl">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="A.I. Means Business" 
                className="h-16 w-auto"
              />
            </div>

            {!isSubmitted ? (
              <>
                {/* Header */}
                <div className="text-center mb-6">
                  <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
                    Forgot Password
                  </h1>
                  <p className="text-slate-600 text-sm">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-navy-900 text-sm font-medium">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="bg-slate-50 border-slate-300 text-navy-900 mt-1 focus:border-primary focus:ring-primary"
                      autoComplete="email"
                      data-testid="forgot-password-email"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary hover:bg-primary/90 text-white"
                    data-testid="forgot-password-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>
                </form>

                {/* Back to Login */}
                <div className="text-center mt-6">
                  <Link 
                    to="/login" 
                    className="text-primary text-sm hover:underline inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  Check Your Email
                </h2>
                <p className="text-slate-600 text-sm mb-6">
                  If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                </p>
                <p className="text-slate-500 text-xs mb-6">
                  The link will expire in 1 hour. If you don't see the email, check your spam folder.
                </p>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="border-slate-300 text-navy-900 hover:bg-slate-100"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Return to Sign In
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ForgotPasswordPage;
